import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Avatar, MasterDetailShell, PaneEmpty, RailSearch, RailTimeline, RailRecents, hl, useToast, useUnsavedGuard } from '../common';
import { daysInclusive, shortDate } from '../../lib/dates';
import { api } from '../../lib/api';
import { LeaveForm } from './LeaveForm';
import { HelperForm } from './HelperForm';
import type { Holiday, LeaveKind, Staff } from './types';

const KIND_LABEL: Record<LeaveKind, string> = { annual: 'Annual', sick: 'Sick', unpaid: 'Unpaid' };

type Mode = 'leave' | 'new' | 'helperEdit';

export interface StaffModalProps {
  staff: Staff[];
  today: string; // ISO
  // One of these seeds the open mode:
  helperId?: string | null; // edit this helper
  holiday?: Holiday | null; // edit this leave entry
  // Like `holiday`, but by id alone — for a caller that knows WHICH leave entry
  // without holding the record. The activity feed is the case: its rows carry
  // the affected holiday's id and its helper, nothing more. Selected once the
  // helper's leave list lands.
  holidayId?: string | null;
  startNewLeave?: boolean; // log new leave (helper = first, switchable)
  createHelper?: boolean; // add a new helper
  // Override the derived initial pane — e.g. open a helper straight into their
  // LEAVE view (search's leave-row click) rather than the default helperEdit.
  initialMode?: Mode;
  onClose: () => void;
  // Present when this modal was opened from somewhere that can be returned to —
  // a global-search result. Absent, the shell renders the arrow disabled.
  onBack?: () => void;
  backLabel?: string;
  onChanged: () => void;
}

// Unified staff modal — the master-detail twin of OwnerBookingModal. Composes
// the shared MasterDetailShell + RailSearch + RailTimeline; only the staff data
// flow (helpers + their leave) and the two panes are staff-specific.
export function StaffModal({ staff, today, helperId, holiday, holidayId, startNewLeave, createHelper, initialMode, onClose, onChanged, onBack, backLabel }: StaffModalProps) {
  // New-leave-from-scratch seeds NO helper — show the empty state and let the user
  // pick one from the rail (don't auto-select staff[0]). wantNew below remembers
  // the intent so the first pick drops straight into the log-leave form.
  const seedHelper =
    (holiday && staff.find((s) => s.id === holiday.staffId)) ||
    (helperId && staff.find((s) => s.id === helperId)) ||
    null;
  const [helper, setHelper] = useState<Staff | null>(seedHelper);
  const [leave, setLeave] = useState<Holiday[]>([]);
  const [leaveLoaded, setLeaveLoaded] = useState(false);
  const [sel, setSel] = useState<Holiday | null>(holiday ?? null);
  const [mode, setMode] = useState<Mode>(
    initialMode ?? (createHelper ? 'helperEdit' : helperId ? 'helperEdit' : startNewLeave ? 'new' : 'leave'),
  );
  const [creating, setCreating] = useState(!!createHelper);
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [wantNew, setWantNew] = useState(!!startNewLeave); // log-leave intent, pending a helper pick
  const toast = useToast();

  // Switch generation — guards against a stale /holidays response from a previous
  // helper landing after a newer click and overwriting the current helper's leave.
  const switchGenRef = useRef(0);
  const loadLeave = useCallback(async (staffId: string) => {
    // Scoped to this helper — no fetch-every-holiday-then-filter. The helper is
    // already seeded from the staff prop, so only their leave is hydrated.
    const gen = ++switchGenRef.current;
    setLeaveLoaded(false);
    try {
      const r = await api<Holiday[]>(`/holidays?staffId=${staffId}`, { cache: 'reload' });
      if (gen !== switchGenRef.current) return null; // superseded by a newer switch
      const data = r.data ?? [];
      setLeave(data);
      setLeaveLoaded(true);
      return data;
    } catch { if (gen === switchGenRef.current) setLeaveLoaded(true); return null; /* keep last */ }
  }, []);

  // Opening ONTO a known leave entry: without this, a caller that had only the
  // id landed in leave mode with nothing selected — naming a holiday and then
  // showing none (Codex, PR #149). Ids are compared as strings because holidays
  // use an integer key while the feed carries text.
  useEffect(() => {
    if (!seedHelper) return;
    void loadLeave(seedHelper.id).then((data) => {
      if (!data || !holidayId) return;
      const match = data.find((h) => String(h.id) === String(holidayId));
      if (match) { setSel(match); setMode('leave'); }
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // THROUGH THE GUARD, like every other way out (review on #239). Each form
  // is keyed, so any of these replaces it — and a half-logged leave or an
  // edited helper went with it, unasked.
  const { guard } = useUnsavedGuard();
  const switchHelper = (s: Staff) => guard(() => void switchHelperNow(s));
  const switchHelperNow = async (s: Staff) => {
    setHelper(s); setQuery(''); setFocused(false); setCreating(false);
    const data = await loadLeave(s.id);
    if (data == null) return; // superseded or failed — don't touch this helper's selection
    // Came in to log new leave → first pick drops straight into the log-leave form.
    if (wantNew) { setWantNew(false); setSel(null); setMode('new'); return; }
    const next = [...data].filter((h) => h.toDate >= today).sort((a, b) => a.fromDate.localeCompare(b.fromDate))[0] ?? data[0];
    if (next) { setSel(next); setMode('leave'); } else { setSel(null); setMode('new'); }
  };
  // Edit-save closes the modal; new-create closes too. (onChanged refreshes the page.)
  const afterSave = () => { onChanged(); onClose(); };
  const openLeave = (h: Holiday) => guard(() => { setSel(h); setMode('leave'); });
  const newLeave = () => guard(() => { setSel(null); setMode('new'); });
  const editHelper = () => guard(() => { setCreating(false); setMode('helperEdit'); });
  const startCreate = () => guard(() => { setCreating(true); setMode('helperEdit'); setQuery(''); setFocused(false); });

  const candidates = (query.trim() || focused)
    ? staff.filter((s) => !query.trim() || s.name.toLowerCase().includes(query.toLowerCase()) || s.role.toLowerCase().includes(query.toLowerCase()))
    : [];
  // 10 most recently-added staff (ids are s<N>, so a higher N is newer). Shown in
  // the rail when there's no helper-leave timeline yet (creating / nothing picked).
  const recentStaff = [...staff]
    .sort((a, b) => (parseInt(b.id.slice(1)) || 0) - (parseInt(a.id.slice(1)) || 0))
    .slice(0, 10);

  const left = helper ? Math.max(0, helper.holsTotal - helper.holsUsed) : 0;

  const renderNode = (h: Holiday): ReactNode => {
    const on = mode === 'leave' && sel?.id === h.id;
    const d = daysInclusive(h.fromDate, h.toDate);
    return (
      <button key={h.id} type="button" className={'node' + (on ? ' on' : '')} onClick={() => openLeave(h)}>
        <div className="spine"><div className="dot" /></div>
        <div className="lab"><div className="txt"><div className="rng">{shortDate(h.fromDate)} – {shortDate(h.toDate)}</div><div className="sb"><span className={'tag ' + h.kind}>{KIND_LABEL[h.kind]}</span> <span>{d} {d === 1 ? 'day' : 'days'}</span></div></div><span className="pencil">✎</span></div>
      </button>
    );
  };

  const currentCard = helper && !creating && (
    <button type="button" className={'curowner' + (mode === 'helperEdit' ? ' active' : '')} onClick={editHelper} title="Edit helper record">
      <Avatar name={helper.name} id={helper.id} size={32} />
      <div style={{ minWidth: 0 }}>
        <div className="nm">{helper.name}</div>
        <div className="mt">{helper.role} · {left} days left</div>
      </div>
      <span className="pencil">✎</span>
    </button>
  );

  const rail = (
    <>
      <RailSearch<Staff>
        placeholder="Search helpers…"
        query={query} setQuery={setQuery} focused={focused} setFocused={setFocused}
        items={candidates} idOf={(s) => s.id} isCurrent={(s) => s.id === helper?.id}
        onPick={switchHelper} restLabel="Helpers" addLabel="Add new helper"
        onAdd={startCreate} current={currentCard} noMatchText="No helper matches"
        showOnFocus={!!helper && !creating}
        renderRow={(s, q) => (
          <>
            <Avatar name={s.name} id={s.id} size={30} />
            <div><div className="nm">{hl(s.name, q)}</div><div className="mt">{hl(s.role, q)}</div></div>
            {s.id === helper?.id ? <span className="curtick">● current</span> : <div className="rt">{Math.max(0, s.holsTotal - s.holsUsed)} days left</div>}
          </>
        )}
      />
      {helper && !creating ? (
        <RailTimeline<Holiday>
          headLabel="Leave" items={leave} dateOf={(h) => h.fromDate} renderNode={renderNode}
          onNew={newLeave} newActive={mode === 'new'} activeKey={mode === 'leave' ? sel?.id ?? null : null}
          loading={!leaveLoaded}
          emptyText="No leave logged. Hit + New to log the first."
        />
      ) : (
        <RailRecents<Staff>
          items={recentStaff}
          idOf={(s) => s.id}
          isCurrent={(s) => s.id === helper?.id}
          onPick={switchHelper}
          renderRow={(s) => (
            <>
              <Avatar name={s.name} id={s.id} size={30} />
              <div className="rail-rowtext"><div className="nm">{s.name}</div><div className="mt">{s.role}</div></div>
            </>
          )}
        />
      )}
    </>
  );

  let pane: ReactNode;
  if (mode === 'helperEdit') pane = <HelperForm key={creating ? 'new-helper' : 'helper-' + helper?.id} helper={creating ? null : helper} onSaved={afterSave} onClose={onClose} />;
  else if (!helper) pane = <PaneEmpty icon="palm" title="Log leave" hint="Pick a helper from the list to log their leave." />;
  else if (mode === 'new') pane = <LeaveForm key="new-leave" holiday={null} helper={helper} today={today} onSaved={afterSave} onClose={onClose} />;
  else pane = sel ? <LeaveForm key={sel.id} holiday={sel} helper={helper} today={today} onSaved={afterSave} onClose={onClose} /> : <PaneEmpty icon="palm" title="No leave selected" hint="Pick a leave entry from the timeline, or hit + New to log one." />;

  const modeLabel = creating ? 'new helper' : mode === 'helperEdit' ? 'edit helper' : mode === 'new' ? 'new leave' : 'leave';

  return (
    <MasterDetailShell
      className="staff-obx"
      title={creating ? 'New helper' : helper?.name ?? 'Staff'}
      modeLabel={modeLabel}
      onClose={onClose}
      rail={rail}
      onBack={onBack}
      // This modal keeps no internal stack, so its only back IS the way out.
      backExits={!!onBack}
    >
      {pane}
    </MasterDetailShell>
  );
}
